import React from 'react';
import { Link } from 'react-router-dom';
import {
  MapPin,
  DollarSign,

  Navigation,
  Lock,
  Check,
  Building2,
  Home,
  Images,
  FileText,
  Flame,
  Timer,
  BadgeCheck,
  ArrowRight,
} from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { canBuyLeads, coverImageFor } from '@/lib/roles';
import { locationLabel } from '@/lib/listingSettings';
import CoverImage from '@/components/CoverImage';
import { money, tradeLabel, localizedSlug, LEAD_PRICE, typicalDurationLabel } from '@/data/constants';




export interface Listing {
  id: string;
  slug: string;
  title: string;
  description?: string;
  trade: string;
  /** NULL on the public feed — city is only returned after the lead is bought. */
  city?: string | null;
  state: string;
  zip?: string | null;
  budget_range?: string;
  target_date?: string;
  project_type: string;
  source_type: string;
  lead_price: number;
  status?: string;
  created_at?: string;
  owner_id?: string | null;
  photos?: string[] | null;
  documents?: unknown;
  /** Provided by the mkt_listings_public view. */
  document_count?: number;
  location_locked?: boolean;
  /**
   * LEAD CAPACITY. A lead may be shared with at most `max_contractors` (6)
   * contractors; `contractor_count` is maintained by the database inside the
   * same locked transaction that grants access. Deliberately NOT rendered on
   * public cards — see the note in the card body.
   */
  contractor_count?: number;
  max_contractors?: number;
}



export const daysAgo = (iso?: string) => {
  if (!iso) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));
};

/** Deterministic pseudo distance so the UI always shows a stable value per listing. */
export const pseudoDistance = (id: string) => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) % 997;
  return 3 + (h % 42);
};

interface Props {
  job: Listing;
  unlocked?: boolean;
  onUnlock?: (job: Listing) => void;
}

const JobCard: React.FC<Props> = ({ job, unlocked, onUnlock }) => {
  const { t, path, lang } = useI18n();
  const { profile } = useAuth();
  // NOTE: the listing age (created_at) is intentionally NOT derived or rendered.

  const href = path(`/jobs/${localizedSlug(job.slug, job.trade, lang)}`);

  const photos = (job.photos || []).filter(Boolean);
  const { src: cover, isRealPhoto: hasPhoto } = coverImageFor(job);

  // The public feed returns city = NULL. A row only carries a real city once the
  // viewer owns it, has purchased the lead, or is an admin, so "do we have a
  // city?" is the honest signal for whether this lead is still locked.
  const locationHidden = !unlocked && !job.city;
  const docCount = job.document_count ?? 0;

  // Homeowners browse the feed read-only: no lead price, no unlock button.
  const showLeadTools = !profile || canBuyLeads(profile.user_type);

  // URGENT = the poster's own target date is within 14 days. Derived from real
  // data on the row; never a random or rotating badge, because a fake urgency
  // flag is a dark pattern and erodes exactly the trust this marketplace sells.
  const daysToTarget = job.target_date
    ? Math.floor((new Date(job.target_date).getTime() - Date.now()) / 86400000)
    : null;
  const isUrgent = daysToTarget !== null && daysToTarget >= 0 && daysToTarget <= 14;

  // Typical trade timeline — an editorial planning estimate, labelled as such.
  const timeline = typicalDurationLabel(job.trade, lang);

  // Every listing on the public feed belongs to a registered, email-confirmed
  // account (anonymous posting does not exist), so this badge states a fact.
  const posterLabel = job.source_type === 'gc' ? t('jobs.byGc') : t('jobs.byHomeowner');

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-card transition-all duration-200 hover:-translate-y-1 hover:border-slate-300 hover:shadow-card-hover">
      <Link to={href} className="relative block w-full">
        {/*
          COVER IMAGE — always a real photograph.
          upload -> trade/category library -> branded marketplace cover
          (src/lib/coverImage.ts), and CoverImage repeats that chain at runtime
          via onError so a dead storage URL can never paint a broken icon.
          No dimming: a category cover is a real, professionally shot photo, so
          the feed looks equally alive whether or not the poster uploaded
          pictures. `aspect-[4/3]` + object-cover keeps one card shape on every
          breakpoint with no stretching.
        */}
        <CoverImage
          listing={job}
          alt={hasPhoto ? job.title : ''}
          aspect="aspect-[4/3]"
          className="rounded-none"
        >
          <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-950/45 via-transparent to-transparent" />

          {photos.length > 1 && (
            <span className="absolute bottom-2 right-2 flex items-center gap-1 rounded-full bg-slate-900/75 px-2 py-1 text-[11px] font-bold text-white backdrop-blur">
              <Images className="h-3 w-3" />
              {photos.length}
            </span>
          )}

          {/* Honest labelling: shown ONLY when the photo is not the poster's own. */}
          {!hasPhoto && (
            <span className="absolute bottom-2 right-2 rounded-full bg-slate-900/70 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-white backdrop-blur">
              {t('cover.category')}
            </span>
          )}

          <div className="absolute left-2 top-2 flex flex-wrap items-center gap-1.5">
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold shadow-sm ${
                job.project_type === 'commercial' ? 'bg-brand-blue text-white' : 'bg-emerald-600 text-white'
              }`}
            >
              {job.project_type === 'commercial' ? <Building2 className="h-3 w-3" /> : <Home className="h-3 w-3" />}
              {job.project_type === 'commercial' ? t('common.commercial') : t('common.residential')}
            </span>
            {isUrgent && (
              <span className="inline-flex items-center gap-1 rounded-full bg-brand-orange px-2.5 py-1 text-[11px] font-bold text-white shadow-sm">
                <Flame className="h-3 w-3" /> {t('jobs.urgent')}
              </span>
            )}
          </div>
        </CoverImage>
      </Link>


      <div className="flex flex-1 flex-col p-5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex w-fit items-center rounded-full bg-brand-navy px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-white">
            {tradeLabel(job.trade, lang)}
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-700 ring-1 ring-emerald-200">
            <BadgeCheck className="h-3 w-3" /> {t('jobs.verifiedPoster')}
          </span>
        </div>

        <Link to={href} className="mt-3 block">
          <h3 className="line-clamp-2 text-base font-bold leading-snug text-slate-900 transition-colors group-hover:text-brand-orange">
            {job.title}
          </h3>
        </Link>

        <div className="mt-3 grid gap-1.5 text-sm text-slate-600">
          <p className="flex items-center gap-2">
            <MapPin className="h-4 w-4 shrink-0 text-slate-400" />
            <span className={locationHidden ? 'font-semibold text-slate-700' : ''}>
              {locationLabel({ city: job.city, state: job.state, unlocked: !locationHidden })}
            </span>
            <span className="ml-auto flex items-center gap-1 text-xs text-slate-400">
              <Navigation className="h-3 w-3" />
              {pseudoDistance(job.id)} {t('jobs.miles')}
            </span>
          </p>
          <p className="flex items-center gap-2">
            <DollarSign className="h-4 w-4 shrink-0 text-slate-400" />
            <span className="font-semibold text-slate-800">{job.budget_range || '—'}</span>
          </p>
          {timeline && (
            <p className="flex items-center gap-2">
              <Timer className="h-4 w-4 shrink-0 text-slate-400" />
              <span className="text-slate-500">{t('jobs.typicalTimeline')}:</span>
              <span className="font-semibold text-slate-700">{timeline}</span>
            </p>
          )}
          {/*
            NO POSTED / AGE INDICATOR.
            The age of a lead is deliberately never shown to contractors, so a
            listing is judged on its scope, budget and location instead of on
            how long it has been on the marketplace.
          */}
          <p className="flex items-center justify-end gap-2 text-xs text-slate-400">{posterLabel}</p>

          {docCount > 0 && (
            <p className="flex items-center gap-2 text-slate-500">
              <FileText className="h-4 w-4 shrink-0 text-slate-400" />
              {t('docs.count', { n: docCount })}
            </p>
          )}
        </div>

        {locationHidden && (
          <p className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
            <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {t('jobs.cityHidden')}
          </p>
        )}

        <div className="mt-auto pt-4">
          <div className="flex items-center justify-between gap-3 border-t border-slate-100 pt-4">
            {showLeadTools ? (
              <>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                    {t('jobs.leadPrice')}
                  </p>
                  <p className="text-lg font-extrabold text-slate-900">{money(LEAD_PRICE)}</p>
                </div>
                {unlocked ? (
                  <Link
                    to={href}
                    className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-emerald-700"
                  >
                    <Check className="h-4 w-4" /> {t('jobs.unlocked')}
                  </Link>
                ) : (
                  /*
                    EVERY available lead carries an explicit purchase action.
                    Contractors holding credits see "Unlock for 1 Credit";
                    everyone else sees "Buy This Lead" next to the flat $29
                    price shown on the left. The detail page then decides
                    between spending a credit and taking the $29 payment —
                    and the server re-checks both.
                  */
                  <button
                    type="button"
                    onClick={() => onUnlock?.(job)}
                    style={{ touchAction: 'manipulation' }}
                    className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-brand-orange px-4 py-2.5 text-sm font-bold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-orange-600 hover:shadow-glow focus:outline-none focus-visible:ring-4 focus-visible:ring-orange-300"
                  >
                    <Lock className="h-4 w-4" />
                    {(profile?.extra_credits || profile?.credits || 0) > 0
                      ? t('jobs.unlockWithCredit')
                      : t('pay.buyThisLead')}
                  </button>
                )}

              </>
            ) : (
              <Link
                to={href}
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-slate-300 px-4 py-2.5 text-center text-sm font-bold text-slate-800 transition-colors hover:border-slate-400 hover:bg-slate-50"
              >
                {t('jobs.viewDetails')} <ArrowRight className="h-4 w-4" />
              </Link>
            )}
          </div>

          {/* Secondary "View details" so every card has a non-purchase path. */}
          {showLeadTools && (
            <Link
              to={href}
              className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900"
            >
              {t('jobs.viewDetails')} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          )}
        </div>
      </div>
    </article>
  );
};

export default JobCard;

